import { stegaClean } from "@sanity/client/stega";
import Image from "next/image";
import Link from "next/link";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { type IconName } from "@/components/ui/icons";
import { icon } from "@/components/ui/icons";
import { type HrefObject } from "@/lib/types";
import { cn } from "@/lib/ui/cn";
import { applyColorTheme, type ColorTheme } from "@/lib/ui/colorTheme";
import { resolveHref, urlForImageCached } from "@/sanity/lib/utils";

import { cleanModuleProps } from "../modules/utils";

type CtaText = {
  label: string;
  href: HrefObject | string; // Support both new object structure and legacy string
  colorTheme?: ColorTheme;
  iconName?: IconName | string;
  titleIcon?: { asset: { url: string } } | null;
};

type CtaButton = {
  label: string;
  href: HrefObject | string; // Support both new object structure and legacy string
  variant?: "primary" | "secondary" | "tertiary" | "link";
  size?: "default" | "sm";
  iconName?: IconName | string;
  iconPosition?: "left" | "right";
  colorTheme?: ColorTheme;
  titleIcon?: { asset: { url: string } } | null;
};

type CtaLink = {
  label: string;
  href: HrefObject | string; // Support both new object structure and legacy string
};

/**
 * Resolves href from the href object structure (internal reference or manual URL)
 */
export function resolveCtaHref(href: HrefObject | string | null | undefined): string | null {
  if (!href) return null;

  // Legacy string support
  if (typeof href === "string") {
    return stegaClean(href);
  }

  // New object structure
  if (href.linkType === "internal" && href.internalReference) {
    // Handle both reference structure (from types) and dereferenced structure (from GROQ)
    const ref = href.internalReference as any;
    const documentType =
      ref._type === "page" || (ref._type === "reference" && ref._ref?.includes("page"))
        ? "page"
        : ref._type === "contentHub" ||
            (ref._type === "reference" && ref._ref?.includes("contentHub"))
          ? "contentHub"
          : ref._type === "productPage" ||
              (ref._type === "reference" && ref._ref?.includes("productPage"))
            ? "productPage"
            : undefined;

    // Handle slug - can be string (from GROQ slug.current) or object with current property
    const slug = typeof ref.slug === "string" ? ref.slug : ref.slug?.current;
    const resolvedHref = resolveHref(documentType, slug);
    if (!resolvedHref) return null;

    // Append hash fragment if provided
    const hash = stegaClean(href.hash || "");
    return hash.length > 0 ? `${resolvedHref}#${hash}` : resolvedHref;
  }

  if (href.linkType === "manual" && href.manualUrl) {
    return href.manualUrl;
  }

  return null;
}

/**
 * Renders a CTA as a text link with optional color theming
 */
export function CtaText({ cta, className }: { cta: CtaText; className?: string }) {
  const href = resolveCtaHref(cta?.href);
  const themeClasses = applyColorTheme(cta?.colorTheme);

  if (!cta?.label || !href) return null;

  const iconName = cta.iconName;
  const IconComponent = iconName ? icon(iconName as IconName) : null;
  const titleIconUrl = cta.titleIcon
    ? urlForImageCached(cta.titleIcon as any)
        ?.width(20)
        .height(20)
        .url()
    : null;

  // Priority: titleIcon > iconName
  const iconElement =
    titleIconUrl && typeof titleIconUrl === "string" ? (
      <Image
        src={titleIconUrl}
        alt=""
        width={20}
        height={20}
        className="h-5 w-5 shrink-0 object-contain"
      />
    ) : IconComponent ? (
      React.createElement(IconComponent, { className: "shrink-0 size-5" })
    ) : null;

  return (
    <Link
      href={href}
      className={cn(
        "inline-flex items-center gap-2 transition-colors hover:underline focus:underline",
        themeClasses,
        className,
      )}
    >
      {iconElement}
      {cta.label}
    </Link>
  );
}

/**
 * Renders a CTA as a button with full button variant support
 *
 * To force a light/dark theme, wrap the button in a container with the appropriate theme class.
 * The button component uses Tailwind's `light:` variant which activates when a parent has the `light` class.
 */
export function CtaButton({ cta, className }: { cta: CtaButton; className?: string }) {
  const href = resolveCtaHref(cta?.href);

  const [variant, size, iconPosition, iconName, colorTheme, titleIcon] = cleanModuleProps(cta, [
    "variant",
    "size",
    "iconPosition",
    "iconName",
    "colorTheme",
    "titleIcon",
  ]);
  const variantValue =
    (variant as "primary" | "secondary" | "tertiary" | "link" | undefined) || "primary";
  const sizeValue = (size as "default" | "sm" | undefined) || "default";
  const iconPositionValue = (iconPosition as "left" | "right" | undefined) || "right";
  const iconNameValue = (iconName as IconName | string | undefined) || undefined;
  const colorThemeValue = (colorTheme as ColorTheme | undefined) || undefined;

  if (!cta?.label || !href) return null;

  // Priority: titleIcon > iconName
  const IconComponent = iconNameValue ? icon(iconNameValue as IconName) : undefined;
  const titleIconUrl = titleIcon
    ? urlForImageCached(titleIcon as any)
        ?.width(20)
        .height(20)
        .url()
    : null;

  const iconElement =
    titleIconUrl && typeof titleIconUrl === "string" ? (
      <Image src={titleIconUrl} alt="" width={20} height={20} className="h-5 w-5 object-contain" />
    ) : IconComponent ? (
      React.createElement(IconComponent)
    ) : undefined;

  const button = (
    <Button
      asChild
      variant={variantValue}
      size={sizeValue}
      icon={iconElement}
      iconPosition={iconPositionValue}
      className={className}
    >
      <Link href={href}>{cta.label}</Link>
    </Button>
  );

  // Wrap button in a container with `light` class to force light theme
  // The button component uses `light:` prefixed classes that activate when parent has `light` class
  if (colorThemeValue === "light") {
    return <div className="light">{button}</div>;
  }

  // For dark theme or no theme specified, return button as-is (dark is default)
  return button;
}

/**
 * Renders a CTA as a link
 */
export function CtaLink({ cta, className }: { cta: CtaLink; className?: string }) {
  const href = resolveCtaHref(cta?.href);

  if (!cta?.label || !href) return null;

  return (
    <Link
      href={href}
      className={cn(
        "text-emerald-light inline-flex items-center gap-2 underline transition-colors",
        className,
      )}
    >
      {cta.label}
    </Link>
  );
}

type CtaFlexible = {
  ctaType: "text" | "button";
  label: string;
  href: HrefObject | string;
  textColorTheme?: ColorTheme;
  variant?: "primary" | "secondary" | "tertiary" | "link";
  size?: "default" | "sm";
  iconName?: IconName | string;
  iconPosition?: "left" | "right";
  buttonColorTheme?: ColorTheme;
  titleIcon?: { asset: { url: string } } | null;
};

/**
 * Renders a flexible CTA that can be either text or button based on ctaType
 */
export function CtaFlexible({ cta, className }: { cta: CtaFlexible; className?: string }) {
  if (!cta) return null;

  const [
    ctaType,
    variant,
    size,
    iconName,
    iconPosition,
    buttonColorTheme,
    textColorTheme,
    titleIcon,
  ] = cleanModuleProps(cta, [
    "ctaType",
    "variant",
    "size",
    "iconName",
    "iconPosition",
    "buttonColorTheme",
    "textColorTheme",
    "titleIcon",
  ]);
  const ctaTypeValue = (ctaType as "text" | "button" | "link" | undefined) || "button";
  const variantValue =
    (variant as "primary" | "secondary" | "tertiary" | "link" | undefined) || "primary";
  const sizeValue = (size as "default" | "sm" | undefined) || "default";
  const iconNameValue = (iconName as IconName | string | undefined) || undefined;
  const iconPositionValue = (iconPosition as "left" | "right" | undefined) || "right";
  // Force left position when titleIcon is present
  const finalIconPosition = titleIcon ? "left" : iconPositionValue;
  const buttonColorThemeValue = (buttonColorTheme as ColorTheme | undefined) || undefined;
  const textColorThemeValue = (textColorTheme as ColorTheme | undefined) || undefined;

  if (ctaTypeValue === "button") {
    return (
      <CtaButton
        cta={{
          label: cta.label,
          href: cta.href,
          variant: variantValue,
          size: sizeValue,
          iconName: iconNameValue,
          iconPosition: finalIconPosition,
          colorTheme: buttonColorThemeValue,
          titleIcon: titleIcon as any,
        }}
        className={className}
      />
    );
  }

  if (ctaTypeValue === "link") {
    return (
      <CtaLink
        cta={{
          label: cta.label,
          href: cta.href,
        }}
        className={className}
      />
    );
  }

  return (
    <CtaText
      cta={{
        label: cta.label,
        href: cta.href,
        colorTheme: textColorThemeValue,
        iconName: cta.iconName, // Text can also have icons
        titleIcon: titleIcon as any,
      }}
      className={className}
    />
  );
}

/**
 * Renders either a CTA text or button based on the type
 */
export function Cta({
  cta,
  className,
}: {
  cta:
    | CtaText
    | CtaButton
    | CtaFlexible
    | ({ _type: "ctaText" | "ctaButton" | "ctaFlexible" } & (CtaText | CtaButton | CtaFlexible));
  className?: string;
}) {
  if (!cta) return null;

  // Check for flexible CTA type
  if ("ctaType" in cta) {
    return <CtaFlexible cta={cta as CtaFlexible} className={className} />;
  }

  // Type guard to determine if it's a button or text CTA
  const isButton = "_type" in cta ? cta._type === "ctaButton" : "variant" in cta;

  if (isButton) {
    return <CtaButton cta={cta as CtaButton} className={className} />;
  }

  return <CtaText cta={cta as CtaText} className={className} />;
}
